import { useEffect, useRef, useState } from "react";

import { withoutCanvasRendering } from "./canvasRendering";
import {
    buildFileBufferObject,
    FileBufferObject,
    MAX_BROWSER_UPLOAD_BYTES,
} from "./components/bufferFileUpload";
import { gameBoyModes } from "./components/modeSwitch";
import {
    applySaveState,
    EmulatorSettings,
    encodeSaveState,
    enableEsp8266 as wasmEnableEsp8266,
    esp8266DiscardPendingCommand,
    esp8266PushResponse,
    initializeEmulator,
    registerGamegenieCheat,
    registerGamesharkCheat,
    resetEmulator,
} from "./core/retroboyCore";
import {
    initEsp8266CommandHandler,
    resetEsp8266Session,
} from "./esp8266/commandHandler";
import { isElfFile, parseElfFile } from "./esp8266/elfParser";
import { parseInoFiles } from "./esp8266/inoParser";
import { parseNanuFile } from "./esp8266/nanuParser";
import { setApiToken, setApiUrl, setParsedData } from "./esp8266/textData";
import { canStartRom, EspLoadState, modeForRomHeader } from "./espLoadLogic";
import useAudioSync from "./hooks/useAudioSync";
import { useKeyListeners } from "./hooks/useKeyListeners";
import { useIsMobile } from "./hooks/useResponsiveBreakpoint";
import { useSettingsStore } from "./hooks/useSettingsStore";
import { useTopLevelRenderer } from "./hooks/useTopLevelRenderer";
import { CheatType } from "./modals/cheatsModal";
import { CheatsModal } from "./modals/cheatsModal";
import { ControlsModal } from "./modals/controlsModal";
import { MessageDialog } from "./modals/dialog";
import { ImportBackupModal } from "./modals/importBackupModal";
import { buildImportOptionsFromBackupJson } from "./modals/importOptionsLogic";
import { LicenseModal } from "./modals/licenseModal";
import FullscreenView from "./views/fullscreenView";
import StandardView from "./views/standardView";

function isDaydreamRom(data: Uint8Array): boolean {
    const title = "Daydream";
    for (let i = 0; i < title.length; i++) {
        if (data[0x134 + i] !== title.charCodeAt(i)) {
            return false;
        }
    }
    return true;
}

const errorModalKey = "error-modal";
const MAX_BUNDLED_ESP_BYTES = 2 * 1024 * 1024;
const BUNDLED_ESP_TIMEOUT_MS = 15_000;
const controlsModalKey = "controls-modal";
const cheatsModalKey = "cheats-modal";
const importBackupModalKey = "import-backup-modal";
const licenseModalKey = "license-modal";

const Interface = (): JSX.Element => {
    const isMobile = useIsMobile();

    const { displayTopLevelComponent, removeTopLevelComponent } =
        useTopLevelRenderer();

    const [rom, setRom] = useState(null as FileBufferObject | null);
    const [inoLoaded, setInoLoaded] = useState(false);
    const [espLoadState, setEspLoadState] = useState<EspLoadState>("idle");
    const manualEspLoaded = useRef(false);
    const espLoadRequest = useRef(0);
    const manualEspLoadRequest = useRef(0);
    const espBundleAbortController = useRef<AbortController | null>(null);

    const [gameKey, setGameKey] = useState(null as string | null);
    const [playing, setPlaying] = useState(false);
    const [paused, setPaused] = useState(false);
    const [mode, setMode] = useState(gameBoyModes.dmg);
    const [fullscreenMode, setFullscreenMode] = useState(false);
    const [usingModal, setUsingModal] = useState(false);

    useKeyListeners(playing, usingModal);

    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const loadStateRef = useRef<HTMLInputElement>(null);
    const importBackupRef = useRef<HTMLInputElement>(null);

    const { settings, storeSettings } = useSettingsStore();
    const volume = settings.volume ?? 1.0;

    const handleVolumeChange = (newVolume: number): void => {
        storeSettings({ ...settings, volume: newVolume });
    };

    const resetGame = (): void => {
        espLoadRequest.current++;
        manualEspLoadRequest.current++;
        espBundleAbortController.current?.abort();
        espBundleAbortController.current = null;
        resetEsp8266Session();
        setGameKey(null);
        setPlaying(false);
        setPaused(false);
        resetEmulator();
        setRom(null);
        setEspLoadState(manualEspLoaded.current ? "ready" : "idle");
        setInoLoaded(manualEspLoaded.current);
    };

    const [audioContextRef] = useAudioSync(playing, volume, resetGame);

    const scrollToGamePad = ({ smooth }: { smooth: boolean }) => {
        window.scrollTo({
            top: document.body.scrollHeight,
            behavior: smooth ? "smooth" : "auto",
        });
    };

    // ESP8266エミュレーション初期化（一度だけ）
    useEffect(() => {
        initEsp8266CommandHandler(
            (bytes: Uint8Array) => {
                esp8266PushResponse(bytes);
            },
            () => esp8266DiscardPendingCommand(),
        );
        return () => {
            espBundleAbortController.current?.abort();
            resetEsp8266Session();
        };
    }, []);

    const handleInoFilesSelect = async (files: File[]): Promise<void> => {
        const requestId = ++manualEspLoadRequest.current;
        // Abortのrejectが先に走っても、旧bundle側がエラーUIを出さないよう
        // request idを無効化してから通信を止める。
        espLoadRequest.current++;
        espBundleAbortController.current?.abort();
        espBundleAbortController.current = null;
        resetEsp8266Session();
        try {
            if (files.some(file => file.size > MAX_BROWSER_UPLOAD_BYTES)) {
                throw new Error("ESP8266 data is too large");
            }
            let data;
            const elfFile = files.find(f => f.name.endsWith(".elf"));
            if (elfFile) {
                const buf = await elfFile.arrayBuffer();
                if (!isElfFile(buf)) throw new Error("Invalid ELF file");
                data = parseElfFile(buf);
            } else {
                data = await parseInoFiles(files);
            }
            if (requestId !== manualEspLoadRequest.current) return;

            setParsedData(data);
            if (data.apiToken) setApiToken(data.apiToken);
            if (data.apiUrl) setApiUrl(data.apiUrl);
            wasmEnableEsp8266();
            manualEspLoaded.current = true;
            setInoLoaded(true);
            setEspLoadState("ready");
        } catch (err) {
            if (requestId !== manualEspLoadRequest.current) return;

            openErrorDialog("Failed to load ESP8266 data.");
            console.error(err);
        }
    };

    const registerCheats = (newGameKey: string): void => {
        const cheatsById = settings.cheats ? settings.cheats[newGameKey] : {};
        const cheats = Object.values(cheatsById || {});
        cheats.forEach(cheat => {
            if (cheat.registered) {
                if (cheat.type == CheatType.GameShark) {
                    registerGamesharkCheat(cheat.id, cheat.code);
                } else {
                    registerGamegenieCheat(cheat.id, cheat.code);
                }
            }
        });
    };

    const playGame = () => {
        if (rom) {
            if (!canStartRom(isDaydreamRom(rom.data), espLoadState)) {
                openErrorDialog(
                    espLoadState === "error"
                        ? "ESP8266 data could not be loaded."
                        : "ESP8266 data is still loading.",
                );
                return;
            }
            if (!audioContextRef.current) {
                audioContextRef.current = new AudioContext();
            }
            void audioContextRef.current.resume();

            const emulatorSettings = new EmulatorSettings(
                mode,
                audioContextRef.current.sampleRate,
            );
            const { error, metadata } = withoutCanvasRendering(() =>
                initializeEmulator(rom.data, emulatorSettings),
            );

            if (error) {
                openErrorDialog(error);
                resetGame();
            } else if (metadata) {
                if (inoLoaded) wasmEnableEsp8266();
                setGameKey(metadata.title);
                registerCheats(metadata.title);
                setPlaying(true);
                scrollToGamePad({ smooth: true });
            }
        }
    };

    const pauseGame = (): void => {
        setPaused(true);
        setPlaying(false);
    };

    const resumeGame = (): void => {
        setPaused(false);
        setPlaying(true);
        scrollToGamePad({ smooth: true });
    };

    const onFullscreenChange = (): void => {
        if (!document.fullscreenElement) {
            exitFullscreen();
        }
    };

    const exitFullscreen = (): void => {
        setFullscreenMode(false);
        if (document.exitFullscreen) {
            document.exitFullscreen().catch(() => {});
        }
    };

    const downloadScreenshot = (): void => {
        if (canvasRef.current) {
            const dataUrl = canvasRef.current.toDataURL("image/png");
            const link = document.createElement("a");
            link.href = dataUrl;
            link.download = "retroboy-screenshot.png";
            link.click();
        }
    };

    const openErrorDialog = (message: string): void => {
        displayTopLevelComponent(
            errorModalKey,
            <MessageDialog
                heading="Error"
                message={message}
                onClose={() => removeTopLevelComponent(errorModalKey)}
            />,
        );
    };

    const openControls = (): void => {
        setUsingModal(true);
        displayTopLevelComponent(
            controlsModalKey,
            <ControlsModal
                onClose={() => {
                    removeTopLevelComponent(controlsModalKey);
                    setUsingModal(false);
                }}
            />,
        );
    };

    const handleRomChange = (fileObject: FileBufferObject | null): void => {
        const requestId = ++espLoadRequest.current;
        manualEspLoadRequest.current++;
        espBundleAbortController.current?.abort();
        espBundleAbortController.current = null;
        resetEsp8266Session();

        // 実行中のROMを差し替える場合は古いエミュレータループを止める。
        // 新しいDaydreamデータのロード失敗時にもPause不能な旧ROMを残さない。
        if (playing || paused || gameKey) {
            setGameKey(null);
            setPlaying(false);
            setPaused(false);
            resetEmulator();
        }

        if (fileObject) {
            setMode(
                modeForRomHeader(fileObject.data, fileObject.filename, mode),
            );
        }
        setRom(fileObject);

        if (!fileObject || !isDaydreamRom(fileObject.data)) {
            setEspLoadState(manualEspLoaded.current ? "ready" : "idle");
            setInoLoaded(manualEspLoaded.current);
            return;
        }

        if (manualEspLoaded.current) {
            setEspLoadState("ready");
            setInoLoaded(true);
            return;
        }

        setEspLoadState("loading");
        setInoLoaded(false);

        void (async () => {
            const controller = new AbortController();
            espBundleAbortController.current = controller;
            const timeoutId = window.setTimeout(
                () => controller.abort(),
                BUNDLED_ESP_TIMEOUT_MS,
            );
            try {
                const response = await fetch(
                    `${process.env.PUBLIC_URL}/daydream-esp8266.nanu`,
                    { cache: "no-store", signal: controller.signal },
                );
                if (!response.ok) {
                    throw new Error(
                        `ESP8266 data request failed: ${response.status}`,
                    );
                }
                const declaredLength = Number(
                    response.headers?.get("Content-Length") || 0,
                );
                if (declaredLength > MAX_BUNDLED_ESP_BYTES) {
                    throw new Error("ESP8266 data is too large");
                }

                let buffer: ArrayBuffer;
                if (response.body) {
                    const reader = response.body.getReader();
                    const chunks: Uint8Array[] = [];
                    let total = 0;
                    while (true) {
                        const { done, value } = await reader.read();
                        if (done) break;
                        total += value.byteLength;
                        if (total > MAX_BUNDLED_ESP_BYTES) {
                            controller.abort();
                            throw new Error("ESP8266 data is too large");
                        }
                        chunks.push(value);
                    }
                    const bytes = new Uint8Array(total);
                    let offset = 0;
                    for (const chunk of chunks) {
                        bytes.set(chunk, offset);
                        offset += chunk.byteLength;
                    }
                    buffer = bytes.buffer;
                } else {
                    buffer = await response.arrayBuffer();
                    if (buffer.byteLength > MAX_BUNDLED_ESP_BYTES) {
                        throw new Error("ESP8266 data is too large");
                    }
                }
                const data = parseNanuFile(buffer);

                if (requestId !== espLoadRequest.current) return;

                setParsedData(data);
                if (data.apiToken) setApiToken(data.apiToken);
                if (data.apiUrl) setApiUrl(data.apiUrl);
                setInoLoaded(true);
                setEspLoadState("ready");
            } catch (error) {
                if (requestId !== espLoadRequest.current) return;

                setInoLoaded(false);
                setEspLoadState("error");
                openErrorDialog("Failed to load bundled ESP8266 data.");
                console.error("Error loading ESP8266 data:", error);
            } finally {
                window.clearTimeout(timeoutId);
                if (espBundleAbortController.current === controller) {
                    espBundleAbortController.current = null;
                }
            }
        })();
    };

    const openCheats = (): void => {
        if (gameKey) {
            setUsingModal(true);
            displayTopLevelComponent(
                cheatsModalKey,
                <CheatsModal
                    gameKey={gameKey}
                    onClose={() => {
                        removeTopLevelComponent(cheatsModalKey);
                        setUsingModal(false);
                    }}
                />,
            );
        }
    };

    const openLicenses = (): void => {
        setUsingModal(true);
        displayTopLevelComponent(
            licenseModalKey,
            <LicenseModal
                onClose={() => {
                    removeTopLevelComponent(licenseModalKey);
                    setUsingModal(false);
                }}
            />,
        );
    };

    const handleFileUpload = async (
        event: React.ChangeEvent<HTMLInputElement>,
        fileUploadRef: React.RefObject<HTMLInputElement>,
        callback: (file: File) => Promise<void>,
    ): Promise<void> => {
        const fileList = event.target.files;
        if (!fileList) return;
        const file = fileList[0];
        if (file) {
            await callback(file);
            if (fileUploadRef.current) {
                fileUploadRef.current.value = "";
            }
        }
    };

    const downloadFile = (
        content: BlobPart,
        filename: string,
        mimeType: string,
    ): void => {
        const blob = new Blob([content], { type: mimeType });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = filename;
        link.click();
        URL.revokeObjectURL(url);
    };

    const handleLoadStateChange = (
        event: React.ChangeEvent<HTMLInputElement>,
    ): Promise<void> => {
        return handleFileUpload(event, loadStateRef, async (file: File) => {
            try {
                // 読み込み中に旧TALK/NAMEが完了して復元後の状態へ応答を
                // 注入しないよう、ファイルを読む前に世代を更新してabortする。
                resetEsp8266Session();
                const bufferObject = await buildFileBufferObject(file);
                const error = applySaveState(bufferObject.data);
                if (error) {
                    openErrorDialog(error);
                } else if (inoLoaded) {
                    // Rust側は安全のためESP権限をセーブデータから復元しない。
                    // 現在のUIセッションでデータを読み込んだ場合だけ再認可する。
                    wasmEnableEsp8266();
                }
            } catch (err) {
                openErrorDialog(
                    "An error occurred while loading the save state.",
                );
                console.error("Error loading save state:", err);
            }
        });
    };

    const saveState = (): void => {
        if (gameKey && rom) {
            const result = encodeSaveState();
            if (result.error) {
                openErrorDialog(result.error);
            } else if (result.saveState) {
                const saveStateName = rom.filename.split(".")[0];
                const filename = `${saveStateName}.rbs`;
                downloadFile(
                    result.saveState.buffer as ArrayBuffer,
                    filename,
                    "application/octet-stream",
                );
            }
        }
    };

    const openImportBackup = (backupJson: Record<string, unknown>): void => {
        const importOptions = buildImportOptionsFromBackupJson(backupJson);
        if (!importOptions.length) {
            openErrorDialog("This JSON file has no valid data to import.");
        } else {
            setUsingModal(true);
            displayTopLevelComponent(
                importBackupModalKey,
                <ImportBackupModal
                    onClose={() => {
                        removeTopLevelComponent(importBackupModalKey);
                        setUsingModal(false);
                    }}
                    importOptions={importOptions}
                    backupJson={backupJson}
                />,
            );
        }
    };

    const handleImportBackupChange = async (
        event: React.ChangeEvent<HTMLInputElement>,
    ): Promise<void> => {
        return handleFileUpload(event, importBackupRef, async (file: File) => {
            try {
                if (file.size > MAX_BROWSER_UPLOAD_BYTES) {
                    throw new Error("Backup file is too large");
                }
                const text = await file.text();
                const jsonData = JSON.parse(text) as Record<string, unknown>;
                openImportBackup(jsonData);
            } catch (err) {
                openErrorDialog(
                    "Invalid JSON file. Please select a valid backup file.",
                );
                console.error("Error parsing JSON:", err);
            }
        });
    };

    const exportBackup = (): void => {
        const backupData: Record<string, unknown> = {};

        if (!localStorage.length) {
            openErrorDialog("You currently have no stored settings to export.");
        } else {
            for (let i = 0; i < localStorage.length; i++) {
                const key = localStorage.key(i);
                if (key && !key.startsWith("esp8266_")) {
                    const value = localStorage.getItem(key);
                    backupData[key] = value;
                }
            }

            if (!Object.keys(backupData).length) {
                openErrorDialog(
                    "You currently have no non-sensitive settings to export.",
                );
                return;
            }

            const jsonString = JSON.stringify(backupData, null, 2);
            downloadFile(
                jsonString,
                "retroboy-backup.json",
                "application/json",
            );
        }
    };

    useEffect(() => {
        if (playing && !fullscreenMode && isMobile) {
            setTimeout(() => {
                window.requestAnimationFrame(() =>
                    scrollToGamePad({ smooth: false }),
                );
            });
        }
    }, [fullscreenMode]);

    useEffect(() => {
        document.addEventListener("fullscreenchange", onFullscreenChange);
        return () => {
            document.removeEventListener(
                "fullscreenchange",
                onFullscreenChange,
            );
        };
    }, []);

    return fullscreenMode ? (
        <FullscreenView
            playing={playing}
            paused={paused}
            onExitFullscreen={exitFullscreen}
            canvasRef={canvasRef}
        />
    ) : (
        <StandardView
            gameKey={gameKey}
            playing={playing}
            paused={paused}
            rom={rom}
            mode={mode}
            onRomChange={handleRomChange}
            onRomLoadError={openErrorDialog}
            onModeChange={setMode}
            onPlay={playGame}
            onPause={pauseGame}
            onResume={resumeGame}
            onScreenshot={downloadScreenshot}
            onOpenControls={openControls}
            onOpenCheats={openCheats}
            onOpenLicenses={openLicenses}
            onInoFilesSelect={handleInoFilesSelect}
            inoLoaded={inoLoaded}
            espLoadState={espLoadState}
            playDisabled={
                !rom || !canStartRom(isDaydreamRom(rom.data), espLoadState)
            }
            onLoadStateChange={handleLoadStateChange}
            onSaveState={saveState}
            onImportBackupChange={handleImportBackupChange}
            onExportBackup={exportBackup}
            volume={volume}
            onVolumeChange={handleVolumeChange}
            canvasRef={canvasRef}
            loadStateRef={loadStateRef}
            importBackupRef={importBackupRef}
        />
    );
};

export default Interface;
